import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useGlobalFilters } from '../../hooks/useGlobalFilters';
import { tragerCoefficientChartStore } from './TragerCoefficientChartStore';
import { predictionTrendsStore } from "../../Analysis_A/PredictionTrendsStore";
import DataWindowSlider from '../../components/DataWindowSliderPV';
import ChartPointDataModal from '../../components/ChartPointDataModal';
import ActionObjectificationChart from './ActionObjectificationChart';
import { buildZoneBands } from './zoneBands';
import './TragerCoefficientChart.scss';

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ReferenceLine,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";  
import { timelineEventsStore } from '../../stores';



function formatNumber(val) {
  if (val == null) return '';
  if (typeof val === "number") {
    return val.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
  }
  if (!isNaN(Number(val))) {
    return Number(val).toLocaleString("ru-RU", { maximumFractionDigits: 2 });
  }
  return val ?? "";
}

function formatDate(date) {
  if (!date) return "";
  const d = new Date(date * 1000);
  if (isNaN(d.getTime())) return "";
  return format(d, 'dd.MM.yyyy'); // используем date-fns
}

function getGroupNamesByIds(ids) {
  if (!Array.isArray(ids)) return [];
  return ids.map(id => {
    const group = predictionTrendsStore.groups.find(g => String(g.id) === String(id));
    return group ? group.name : id;
  });
}

// Функция для вычисления номера недели года
function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

const eventDateTimestamp = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).getTime();
};

const buildTimelineEventData = (baseData, events) => {
  if (baseData.length === 0 || events.length === 0) return baseData;

  const grouped = new Map();
  events.forEach((event) => {
    const timestamp = eventDateTimestamp(event.date);
    if (timestamp === null) return;
    if (!grouped.has(timestamp)) grouped.set(timestamp, []);
    grouped.get(timestamp).push(event);
  });

  const result = baseData.map((point) => ({ ...point }));
  const firstTimestamp = result[0].dateTimestamp;
  const lastTimestamp = result[result.length - 1].dateTimestamp;

  [...grouped.entries()].sort(([a], [b]) => a - b).forEach(([timestamp, dateEvents]) => {
    if (timestamp < firstTimestamp || timestamp > lastTimestamp) return;

    const exact = result.find((point) => point.dateTimestamp === timestamp);
    if (exact) {
      exact.timelineEvents = dateEvents;
      return;
    }

    const rightIndex = result.findIndex((point) => point.dateTimestamp > timestamp);
    if (rightIndex <= 0) return;
    const left = result[rightIndex - 1];
    const right = result[rightIndex];
    const ratio = (timestamp - left.dateTimestamp) / (right.dateTimestamp - left.dateTimestamp);
    const interpolate = (leftValue, rightValue) => (
      Number.isFinite(leftValue) && Number.isFinite(rightValue)
        ? leftValue + ((rightValue - leftValue) * ratio)
        : leftValue
    );

    result.splice(rightIndex, 0, {
      ...left,
      date: Math.floor(timestamp / 1000),
      dateTimestamp: timestamp,
      dateFormatted: format(new Date(timestamp), 'dd.MM.yyyy'),
      trager_coef: interpolate(left.trager_coef, right.trager_coef),
      verbs_per_nouns_coef: interpolate(left.verbs_per_nouns_coef, right.verbs_per_nouns_coef),
      timelineEvents: dateEvents,
      isTimelineEventPoint: true,
    });
  });

  return result;
};

const TimelineTooltip = ({ active, payload, label, mean, stdDeviation }) => {
  if (!active || !payload?.length) return null;
  const point = payload.find((item) => item.dataKey === 'trager_coef')?.payload || payload[0].payload;
  const value = point?.trager_coef;
  let zone = 'норма';
  if (stdDeviation > 0 && Number.isFinite(value)) {
    const z = Math.abs((value - mean) / stdDeviation);
    if (z > 3) zone = 'сильный выход за норму';
    else if (z > 2) zone = 'выход за норму';
    else if (z > 1) zone = 'малый выход за норму';
  }

  return (
    <div className="timeline-chart-tooltip">
      <strong>{format(new Date(label), 'dd.MM.yyyy')}</strong>
      {Number.isFinite(value) ? (
        <div>Эмоциональная напряжённость: {value.toFixed(2)} ({zone})</div>
      ) : null}
      {(point?.timelineEvents || []).length > 0 ? (
        <div className="timeline-chart-tooltip__events">
          {(point.timelineEvents || []).slice(0, 6).map((event) => (
            <div key={event.id}>
              <b>{event.title}</b>
              {event.owner_username ? ` · ${event.owner_username}` : ''}
              {event.description ? <small>{event.description}</small> : null}
            </div>
          ))}
          {point.timelineEvents.length > 6 ? (
            <div>Ещё событий: {point.timelineEvents.length - 6}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const CHART_WINDOW_SIZE = 365;

const TragerCoefficientChart = observer(() => {
  const globalFilters = useGlobalFilters();
  const store = tragerCoefficientChartStore;
  const [dataWindow, setDataWindow] = React.useState({ start: 0, end: CHART_WINDOW_SIZE });
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [modalDateText, setModalDateText] = React.useState('');
  const ranges = store.ranges || {
    green: [0, 0],
    gray: [[0, 0], [0, 0]],
    orange: [[0, 0], [0, 0]],
    red: [[0, 0], [0, 0]],
  };
  const meanValue = store.meanValue;


  const getMean = () => {
    return store.meanValue;
  }

  const getStdDev = () => {
    return store.stdDeviation || 0;
  }
  
  // Обновляем дневные данные при изменении глобальных фильтров.
  React.useEffect(() => {
    store.setFilters({
      startDate: globalFilters.startDate,
      endDate: globalFilters.endDate,
      selectedGroupIds: globalFilters.selectedGroups,
    });
    store.fetchData();
    
    // eslint-disable-next-line
  }, [globalFilters.filtersString]);


  const handlePointClick = (dataPoint, index, event) => {
    if (!dataPoint || !dataPoint.date) return;
    const startDateObj = new Date(dataPoint.date * 1000);
    const startDate = format(startDateObj, 'yyyy-MM-dd');
    store.fetchDataByDateRange(startDate, startDate);
    setModalDateText(startDate);
    setIsModalOpen(true);
  };
  // Сброс окна данных при обновлении данных
  React.useEffect(() => {
    setDataWindow({ start: 0, end: CHART_WINDOW_SIZE });
  }, [store.data.length]);

  // Обновление окна данных
  const updateWindow = (value) => {
    const newStart = Number(value);
    setDataWindow({ start: newStart, end: newStart + CHART_WINDOW_SIZE });
  };



  const zoneData = (() => {
    const allData = store.data || [];
    const startIndex = Math.min(dataWindow.start, Math.max(allData.length - 1, 0));
    const endIndex = Math.min(dataWindow.end, allData.length);
    const windowedData = allData.slice(startIndex, endIndex);
    const selectedIds = new Set(globalFilters.selectedTimelineEventIds.map(Number));
    const selectedTimelineEvents = timelineEventsStore.events.filter((event) => selectedIds.has(event.id));
    
    const baseData = windowedData.map((item) => {
      const timestamp = new Date(format(new Date(item.date * 1000), 'yyyy-MM-dd') + 'T00:00:00').getTime();
      return ({
      ...item,
      dateTimestamp: timestamp,
      dateFormatted: format(new Date(item.date * 1000), 'dd.MM.yyyy'),
      greenLow: ranges.green[0],
      greenHigh: ranges.green[1],
      grayLow1: ranges.gray[0][0],
      grayHigh1: ranges.gray[0][1],
      grayLow2: ranges.gray[1][0],
      grayHigh2: ranges.gray[1][1],
      orangeLow1: ranges.orange[0][0],
      orangeHigh1: ranges.orange[0][1],
      orangeLow2: ranges.orange[1][0],
      orangeHigh2: ranges.orange[1][1],
      redLow1: ranges.red[0][0],
      redHigh1: ranges.red[0][1],
      redLow2: ranges.red[1][0],
      redHigh2: ranges.red[1][1],
      mean: meanValue,
      });
    });

    return buildTimelineEventData(baseData, selectedTimelineEvents);
  })();

  const visibleValues = zoneData.map((point) => point.trager_coef).filter(Number.isFinite);
  const visibleMin = visibleValues.length ? Math.min(...visibleValues) : store.minValue;
  const visibleMax = visibleValues.length ? Math.max(...visibleValues) : store.maxValue;
  const chartPadding = Math.max((visibleMax - visibleMin) * 0.08, store.stdDeviation * 0.2, 0.01);
  const chartDomain = [visibleMin - chartPadding, visibleMax + chartPadding];
  const zoneBands = buildZoneBands(chartDomain, meanValue, store.stdDeviation);
  const zoneBoundaries = store.stdDeviation > 0
    ? [-3, -2, -1, 1, 2, 3].map((multiplier) => meanValue + multiplier * store.stdDeviation)
      .filter((value) => value > chartDomain[0] && value < chartDomain[1])
    : [];

  const eventMarkers = zoneData
    .filter((point) => point.timelineEvents?.length)
    .map((point) => ({
      timestamp: point.dateTimestamp,
      events: point.timelineEvents,
      isOwn: point.timelineEvents.some((event) => event.is_owner),
    }));

  // Текст диапазона дат для слайдера
  const dateRangeText = useMemo(() => {
    if (zoneData.length === 0) return 'Нет данных';
    const first = zoneData[0];
    const last = zoneData[zoneData.length - 1];
    return `Показаны данные: ${first.dateFormatted} - ${last.dateFormatted}`;
  }, [zoneData]);

  // Экспорт в Excel
  const handleExportCSV = () => {
    const filters = globalFilters.filtersString ? JSON.parse(globalFilters.filtersString) : {};
    const startDate = filters.startDate || "";
    const endDate = filters.endDate || "";
    const groupNames = getGroupNamesByIds(filters.selectedGroups || []);
    const graphTitle = "Эмоциональная_напряженность";
    const fileName = `${graphTitle}_${groupNames.join("_")}_${startDate}_${endDate}.csv`;
    const allData = store.data || [];
  
    // Заголовки
    const headers = ["date", "trager_coef", "verbs_per_nouns_coef_mean"];
  
    // Формируем строки CSV
    const rows = allData.map(d => {
      const dateStr = formatDate(d.date); // уже в формате dd.MM.yyyy
      const tragerCoefStr = formatNumber(d.trager_coef).replace(',', '.'); // заменяем запятую на точку для совместимости
      const verbsPerNounsCoefMeanStr = formatNumber(d.verbs_per_nouns_coef).replace(',', '.'); // заменяем запятую на точку для совместимости
      return `"${dateStr}","${tragerCoefStr}","${verbsPerNounsCoefMeanStr}"`;
    });
  
    // Собираем всё в одну строку
    const csvContent = [headers.join(','), ...rows].join('\n');
  
    // Создаём Blob и скачиваем
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  // Экспорт в PDF
  const handleExportPDF = async () => {
    const filters = globalFilters.filtersString ? JSON.parse(globalFilters.filtersString) : {};
    const startDate = filters.startDate || "";
    const endDate = filters.endDate || "";
    const groupNames = getGroupNamesByIds(filters.selectedGroups || []);
    const graphTitle = "Эмоциональная_напряженность";
    const fileName = `${graphTitle}_${groupNames.join("_")}_${startDate}_${endDate}.pdf`;
    const allData = store.data || [];
    const input = document.querySelector("#trager-chart-container");
    if (!input) { alert("График не найден для экспорта!"); return; }
    const canvas = await html2canvas(input);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("l", "mm", "a4");
    pdf.addImage(imgData, "PNG", 10, 10, 270, 80);
    
    // Создаем заголовки для PDF
    const headers = ["Date", "trager_coef"];
    
    autoTable(pdf, {
      startY: 100,
      head: [headers],
      body: allData.map(d => [formatDate(d.date), formatNumber(d.trager_coef)]),
      styles: { font: "helvetica", fontSize: 10 }
    });
    pdf.save(fileName);
  };

  return (
    <>
      <div style={{ marginBottom: 16 }}>
      <div className="info-block" style={{
          background: '#e3f2fd',
          borderRadius: '12px',
          padding: '20px 28px',
          marginBottom: '12px',
          boxShadow: '0 2px 8px rgba(25, 118, 210, 0.07)'
        }}>
          <h3 style={{ color: '#1976d2', marginTop: 0, marginBottom: 12, fontWeight: 600 }}>
            Справка
          </h3>
          <div>
            <p>График показывает динамику эмоциональной напряженности по выбранным периодам времени. 
          </p>
          <p>Среднее рассчитывается на сервере по доступным значениям коэффициента с учётом количества сообщений в каждом сообществе. Ограничения по длине, пустому тексту и величине коэффициента не применяются.</p>
          <p><h4>Рекомендация</h4>
          Если значения эмоциональной напряженности в какой-то период времени находятся
          в оранжевой или красной зоне, рекомендуется выполнить анализ дискуссий и эмотивный анализ за этот период.
          Значения выше среднего свидетельствуют о росте агрессии, ниже среднего – о росте тревоги и апатии.</p>
          </div>


           <ul style={{ margin: 0, paddingLeft: 22, color: '#111', fontSize: '1.05em', fontWeight: 500 }}>
            <li>
              <span style={{
                display: 'inline-block',
                width: 18,
                height: 12,
                background: '#00C853',
                borderRadius: 3,
                marginRight: 8,
                border: '1px solid #009624',
                verticalAlign: 'middle'
              }}></span>
              <b>Зеленая зона</b> — эмоциональная напряжённость в норме - в пределах 1-го стандартного отклонения от среднего значения
            </li>
            <li>
              <span style={{
                display: 'inline-block',
                width: 18,
                height: 12,
                background: '#B0BEC5',
                borderRadius: 3,
                marginRight: 8,
                border: '1px solid #bdbdbd',
                verticalAlign: 'middle'
              }}></span>
              <b>Серая зона</b> — переходные значения - в пределах 2-х стандартных отклонений от среднего значения
            </li>
            <li>
              <span style={{
                display: 'inline-block',
                width: 18,
                height: 12,
                background: '#FF6D00',
                borderRadius: 3,
                marginRight: 8,
                border: '1px solid #bdbdbd',
                verticalAlign: 'middle'
              }}></span>
              <b>Оранжевая зона</b> — эмоциональная напряжённость вне нормы - в пределах 3-х стандартных отклонений от среднего значения
            </li>
            <li>
              <span style={{
                display: 'inline-block',
                width: 18,
                height: 12,
                background: '#FF1744',
                borderRadius: 3,
                marginRight: 8,
                border: '1px solid #b71c1c',
                verticalAlign: 'middle'
              }}></span>
              <b>Красная зона</b> — сильное отклонение эмоциональной напряжённости - за пределами 3-х стандартных отклонений от среднего значения
            </li>
          </ul>

        </div>
        
        <div style={{
          marginBottom: 10, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'flex-end'
        }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="export-btn" onClick={handleExportCSV}>Экспорт в Excel</button>
            <button className="export-btn" onClick={handleExportPDF}>Экспорт в PDF</button>
          </div>
        </div>

      </div>



      <div id="trager-chart-container" style={{ minHeight: 620 }}>
        {!globalFilters.selectedGroups || globalFilters.selectedGroups.length === 0 ? (
          <div className="text-center text-muted py-4">Нет выбранной группы</div>
        ) : store.error ? (
          <div className="text-center text-muted py-4">
            {store.error.message || store.error.toString()}
          </div>
        ) : store.loading ? (
          <div className="text-center text-muted py-4">Данные загружаются...</div>
        ) : store.data.length > 0 ? (
          <>
            {store.data.length > CHART_WINDOW_SIZE && (
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
                <DataWindowSlider
                  aggregation={'day'}
                  dataWindow={dataWindow}
                  maxDataLength={store.data.length}
                  windowSize={CHART_WINDOW_SIZE}
                  updateWindow={updateWindow}
                  dateRangeText={dateRangeText}
                />
              </div>
            )}
          <div style={{ width: '100%' }}>
          <ResponsiveContainer width="100%" height={620}>
            <LineChart
              data={zoneData}
              margin={{ top: 20, right: 30, left: 90, bottom: 50 }}
            >
              {/* Убрали пунктирную сетку для более чистого вида графика */}
              {/* <CartesianGrid strokeDasharray="3 3" /> */}
              <XAxis
                dataKey="dateTimestamp"
                type="category"
                tickFormatter={(timestamp) => format(new Date(timestamp), 'dd.MM.yyyy')}
                label={{
                  value: 'Дата',
                  position: 'insideBottom',
                  offset: -45,
                  style: { fontWeight: 600 }
                }}
                tick={{ fontSize: 10, angle: -45, textAnchor: 'end', dy: 12 }}
                interval="preserveStartEnd"
                minTickGap={50}
              />
              <YAxis
                domain={chartDomain}
                tickCount={8}
                width={60}
                tickFormatter={(value) => Number(value).toFixed(2)}
                label={{
                  value: 'Эмоциональная напряжённость',
                  angle: -90,
                  position: 'left',
                  offset: 20,
                  fontSize: 14,
                  style: { fontWeight: 600 },
                }}
                tick={{ fontSize: 10 }}
              />
              <Tooltip content={(props) => (
                <TimelineTooltip
                  {...props}
                  mean={getMean()}
                  stdDeviation={getStdDev()}
                />
              )} />
              {eventMarkers.map((marker) => (
                <ReferenceLine
                  key={`timeline-event-${marker.timestamp}`}
                  x={marker.timestamp}
                  stroke={marker.isOwn ? '#1976d2' : '#ef6c00'}
                  strokeOpacity={0.62}
                  strokeWidth={1}
                />
              ))}
               {zoneBands.map((band, index) => (
                 <ReferenceArea
                   key={index}
                   y1={band.from}
                   y2={band.to}
                   fill={band.color}
                   fillOpacity={band.color === '#E57373' ? 0.3 : 0.6}
                 />
               ))}
               {zoneBoundaries.map((value) => (
                 <ReferenceLine key={value} y={value} stroke="#000000" strokeWidth={1} />
               ))}
               <Line
                  type="monotone"
                  dataKey="trager_coef"
                  stroke="#000000"
                  strokeWidth={1}
                  dot={ (props) => {
                    // Явно извлекаем ключевые свойства, включая стили
                    const {
                      cx, cy, index, payload,
                      fill, stroke, strokeWidth, // <-- ВАЖНО: Извлекаем стили
                      ...restProps // Остальные свойства (например, className)
                    } = props;

                    if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;

                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={payload.timelineEvents?.length ? 7 : 2.5}
                        cursor="pointer"
                        // Явно устанавливаем стили, чтобы точка выглядела как ожидалось
                        fill={payload.timelineEvents?.length ? '#111827' : (fill || '#000000')}
                        stroke={stroke || '#fff'}
                        strokeWidth={strokeWidth || 2}
                        // Передаем остальные свойства, которые могут быть важны
                        {...restProps}
                        // onClick убран, так как работает через activeDot
                      />
                    );
                  }}
                  activeDot={{
                    r: 6,
                    fill: '#000000',
                    stroke: '#fff',
                    strokeWidth: 2,
                    cursor: 'pointer',                    
                    onMouseDown: (e, payloadData, index) => {
                      console.log("MouseDown on activeDot:", payloadData, index, e);
                      handlePointClick(payloadData.payload, index, e); // payloadData.payload содержит реальные данные точки
                    },
                  }}
                />
            </LineChart>
          </ResponsiveContainer>
          </div>
                     </>
         ) : (
           <div className="text-center text-muted py-4">Нет данных для отображения</div>
         )}
      </div>
      {store.sourceCount !== null && (
        <div className="mt-2 text-muted small" style={{ textAlign: 'right' }}>
          Обработано записей: {store.sourceCount.toLocaleString('ru-RU')}
        </div>
      )}
      {!store.loading && !store.error && store.data.length > 0 && (
        <ActionObjectificationChart
          allData={store.data}
          data={zoneData}
          onPointClick={handlePointClick}
        />
      )}
      {isModalOpen && (
        <ChartPointDataModal
          date={modalDateText}
          rows={store.dataByDateRange}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
});

export default TragerCoefficientChart;
